

import {
  ColumnFactory,
  createSortableHeaderFormatter,
  ExplorerStyledDisplayGrid,
  FeatureFlags,
  FeatureFlagsContext,
  GraphQLError,
  GridSortProps,
  reportGraphQLErrors,
  UnsortableColumnKey,
  UsualDisplayGridProps,
} from '@tableau/data-catalog-shared';
import * as React from 'react';
import { Messages } from '../../../gen-src/Localize';
import { MetricsDisplayGridRowDataFragment } from '../../../gen-src/queries/MetricsDisplayGridRowDataFragment';
import { MetricsDisplayGridRowDataFragmentWithoutPersonalSpace } from '../../../gen-src/queries/MetricsDisplayGridRowDataFragmentWithoutPersonalSpace';
import { MetricOrderField } from '../../../gen-src/queries/globalTypes';
import { createLinkCellFormatter, createLinkOrTextCellFormatter } from '../datagrid/cellFormatters/LinkCellFormatter';
import { createObfuscatedLinkOrTextCellFormatter } from '../datagrid/cellFormatters/ObfuscatedCellFormatter';
import { MetricsValidSortFields } from '../sorting/sortProviders/MetricsGridSortProvider';

const projectContainerType = 'Project';

export interface MetricsDisplayGridRowData {
  readonly id: string;
  readonly name: string | null;
  readonly vizportalUrlId: string | null;
  readonly viewName: string | null;
  readonly viewPath: string | null;
  readonly workbookName: string | null;
  readonly workbookVizportalUrlId: string | null;
  readonly containerName: string | null;
  readonly containerType: string;
  readonly projectVizportalUrlId: string | null;
  readonly ownerName: string | null;
  readonly ownerUsername: string | null;
  readonly ownerDomain: string | null;
}

export function getRowDataForMetricWithoutPersonalSpace(metric: MetricsDisplayGridRowDataFragmentWithoutPersonalSpace):
  MetricsDisplayGridRowData {

  return {
    id: metric.id,
    name: metric.name,
    vizportalUrlId: metric.vizportalUrlId,
    viewName: metric.underlyingView?.name ?? null,
    viewPath: metric.underlyingView?.path ?? null,
    workbookName: metric.underlyingView?.workbook?.name ?? null,
    workbookVizportalUrlId: metric.underlyingView?.workbook?.vizportalUrlId ?? null,
    containerName: metric.projectName,
    containerType: projectContainerType,
    projectVizportalUrlId: metric.projectVizportalUrlId,
    ownerName: metric.owner?.name ?? null,
    ownerUsername: metric.owner?.username ?? null,
    ownerDomain: metric.owner?.domain ?? null,
  };
}

export function getRowDataForMetric(metric: MetricsDisplayGridRowDataFragment): MetricsDisplayGridRowData {

  return {
    id: metric.id,
    name: metric.name,
    vizportalUrlId: metric.vizportalUrlId,
    viewName: metric.underlyingView?.name ?? null,
    viewPath: metric.underlyingView?.path ?? null,
    workbookName: metric.underlyingView?.workbook?.name ?? null,
    workbookVizportalUrlId: metric.underlyingView?.workbook?.vizportalUrlId ?? null,
    containerName: metric.containerName,
    containerType: metric.containerType,
    projectVizportalUrlId: metric.projectVizportalUrlId,
    ownerName: metric.owner?.name ?? null,
    ownerUsername: metric.owner?.username ?? null,
    ownerDomain: metric.owner?.domain ?? null,
  };
}

type MetricsColumnFactory = ColumnFactory<
  GridSortProps<MetricsValidSortFields>,
  MetricsDisplayGridRowData,
  MetricsValidSortFields
>;

function columns(featureFlags: FeatureFlags): ReadonlyArray<MetricsColumnFactory> {
  const cols: ReadonlyArray<MetricsColumnFactory> = [
    (sortParams) => ({
      name: 'name',
      dataKey: MetricOrderField.NAME,
      headerText: Messages.GridHeaderName(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createObfuscatedLinkOrTextCellFormatter(
        ({ name }) => name === null,
        ({ name }) => name,
        (urls, { vizportalUrlId }) => vizportalUrlId ? urls.metricPath(vizportalUrlId) : null,
      ),
      resizable: true,
      sortable: true,
    }),
    (sortParams) => ({
      name: 'view',
      dataKey: UnsortableColumnKey,
      headerText: Messages.GridHeaderView(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createObfuscatedLinkOrTextCellFormatter(
        ({ viewName }) => viewName === null,
        ({ viewName }) => viewName,
        (urls, { viewPath }) => viewPath ? urls.viewPath(viewPath) : null,
      ),
      resizable: true,
      sortable: false,
    }),
    (sortParams) => ({
      name: 'workbook',
      dataKey: UnsortableColumnKey,
      headerText: Messages.GridHeaderWorkbook(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createObfuscatedLinkOrTextCellFormatter(
        ({ workbookName }) => workbookName === null,
        ({ workbookName }) => workbookName,
        (urls, { workbookVizportalUrlId }) => workbookVizportalUrlId ? urls.workbookLineagePath(workbookVizportalUrlId) : null
      ),
      resizable: true,
      sortable: false,
    }),
    (sortParams) => ({
      name: featureFlags.personalSpaceEnabled ? 'location' : 'project',
      dataKey: UnsortableColumnKey,
      headerText: featureFlags.personalSpaceEnabled ? Messages.GridHeaderLocation() : Messages.GridHeaderProject(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createLinkOrTextCellFormatter(
        ({ containerType, containerName }) => {
          return containerType === projectContainerType ? containerName : Messages.PersonalSpaceName();
        },
        (urls, { projectVizportalUrlId, containerType }) => (projectVizportalUrlId && containerType === projectContainerType ? urls.projectPath(projectVizportalUrlId) : null)
      ),
      resizable: true,
      sortable: false,
    }),
    (sortParams) => ({
      name: 'owner',
      dataKey: UnsortableColumnKey,
      headerText: Messages.Owner(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createLinkCellFormatter(
        // If ownerName, ownerUsername, and ownerDomain are null, there is likely a permission denied issue
        ({ ownerName, ownerUsername }) => ownerName || ownerUsername || '',
        (urls, { ownerUsername, ownerDomain }) => {
          if (ownerUsername && ownerDomain) {
            return urls.userPath(ownerUsername, ownerDomain);
          } else {
            return '';
          }
        }
      ),
      resizable: true,
      sortable: false,
    })
  ];

  return cols.filter(Boolean) as ReadonlyArray<MetricsColumnFactory>;
}


export type MetricsDisplayGridProps =
  UsualDisplayGridProps<MetricsDisplayGridRowData, GraphQLError> &
  GridSortProps<MetricsValidSortFields>;

export function MetricsDisplayGrid(props: MetricsDisplayGridProps): JSX.Element {
  const featureFlags = React.useContext(FeatureFlagsContext);

  const cols = columns(featureFlags).map(columnFactory => columnFactory(props));

  return (
    <ExplorerStyledDisplayGrid
      columns={cols}
      rowStore={props.rowStore}
      emptyGridMessage={props.emptyGridMessage}
      rowCount={props.rowCount}
      renderErrors={reportGraphQLErrors}
      orderBy={props.orderBy}
      updateSortOrder={props.updateSortOrder}
    />
  );
}
